import { Controller } from "@hotwired/stimulus";
import Rails from '@rails/ujs';

// This controller dynamically renders tabbed sections on the homepage—such as
// Saved Contracts and Saved Searches—without requiring a full page reload.
// When a user clicks a tab, the corresponding content is fetched via AJAX
// and injected into the page.
export default class HomepageController extends Controller<HTMLFormElement> {
  static targets = ["tab", "tabContainer"];
  static values = { initialUrl: String }

  declare tabTargets: HTMLAnchorElement[];
  declare tabContainerTarget: HTMLDivElement;
  declare initialUrlValue: string;

  // On controller connect, load the initial tab content via AJAX.
  connect(): void {
    this.loadContent(this.initialUrlValue);
  }

  // Handle tab click: prevent default navigation, apply styles, and load new
  // content.
  handleTabClick(event: Event) {
    event.preventDefault();

    const activeTab = event.currentTarget as HTMLAnchorElement;

    // Update tab styles to reflect the newly active tab.
    this.markTabAsActive(activeTab);

    // Load HTML content for the selected tab via AJAX.
    this.loadContent(activeTab.href);
  }

  // Apply active and inactive styles across tabs.
  markTabAsActive(activeTab: HTMLAnchorElement) {
    this.unselectAllTabs();
    this.selectTab(activeTab);
  }

  // Reset all tabs to their inactive state.
  unselectAllTabs() {
    this.tabTargets.forEach(t => {
      t.classList.add("saved-data-table__nav-tab--default");
      t.classList.remove("saved-data-table__nav-tab--selected");
    });
  }

  // Highlight the currently active tab.
  selectTab(tab: HTMLAnchorElement) {
    tab.classList.add("saved-data-table__nav-tab--selected");
    tab.classList.remove("saved-data-table__nav-tab--default");
  }

  // Perform an AJAX GET request and inject the response HTML into the container.
  loadContent(url: string): void {
    setTimeout(() => {
      void fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
          // CSRF token required for Rails authenticity validation
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          "X-CSRF-TOKEN": Rails.csrfToken(),
        }
      })
        .then(response => response.text())
        .then(html => {
          this.tabContainerTarget.innerHTML = html;
        });
    }, 0);
  }
}
